import * as amplitude from "@amplitude/analytics-browser";
import { ReturnsFlagKey } from "@redotech/feature-flags-keys-returns/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { RETURNS_CONSOLIDATED_SETTINGS_PATH } from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { usePageActions } from "@redotech/redo-web/page-header-slot";
import type { PageAction } from "@redotech/redo-web/page-prop-types";
import { useMemo, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { MerchantRoutesContext } from "../merchant-routes";
import { useTeamFeatureFlag } from "../use-team-feature-flag";
import {
  RETURNS_SETTINGS_TAB_QUERY_PARAM,
  ReturnsSettingsTab,
} from "./returns-settings-tab";

/** Mirrors the merchant app's PlatformEvents entry; kept here so this hook
 * does not pull the app's event catalogue into the common package. */
const EXPERIENCE_SWITCHED_EVENT = "settings/returns-experience-switched";

const LEGACY_STORAGE_KEY = "redo.returns-settings-legacy";
const LEGACY_PREFERENCE_CHANGED = "redo:returns-settings-legacy-changed";

/** Falls back to this when storage is unavailable, so a switch still applies to the tab. */
let inMemoryLegacyPreference = false;

function readLegacyPreference(): boolean {
  try {
    return localStorage.getItem(LEGACY_STORAGE_KEY) === "true";
  } catch {
    return inMemoryLegacyPreference;
  }
}

function writeLegacyPreference(legacy: boolean) {
  inMemoryLegacyPreference = legacy;
  try {
    if (legacy) {
      localStorage.setItem(LEGACY_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    // Private mode or quota exhausted; the in-memory value still applies.
  }
  window.dispatchEvent(new Event(LEGACY_PREFERENCE_CHANGED));
}

function subscribeToLegacyPreference(onChange: () => void) {
  window.addEventListener(LEGACY_PREFERENCE_CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(LEGACY_PREFERENCE_CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export interface ReturnsSettingsExperience {
  /** The team flag is on; Redo staff are not forced on. */
  available: boolean;
  pending: boolean;
  useNewUi: boolean;
  switchToLegacy(): void;
  switchToNew(): void;
}

export function useReturnsSettingsExperience(): ReturnsSettingsExperience {
  const flag = useTeamFeatureFlag(
    ReturnsFlagKey.CONSOLIDATED_RETURNS_SETTINGS,
    { forceEnabledForRedoUsers: false },
  );
  const prefersLegacy = useSyncExternalStore(
    subscribeToLegacyPreference,
    readLegacyPreference,
  );

  const switchTo = useHandler((legacy: boolean) => {
    writeLegacyPreference(legacy);
    amplitude.track(EXPERIENCE_SWITCHED_EVENT, {
      to: legacy ? "legacy" : "new",
    });
  });

  const switchToLegacy = useHandler(() => switchTo(true));
  const switchToNew = useHandler(() => switchTo(false));

  return {
    available: flag.enabled,
    pending: flag.pending,
    useNewUi: flag.enabled && !prefersLegacy,
    switchToLegacy,
    switchToNew,
  };
}

/**
 * Header button on the old returns settings pages for merchants who opted out.
 * Pass the tab that replaced the page, so the merchant lands on their settings
 * rather than on whichever tab happens to be first.
 */
export function useTryNewReturnsSettingsAction(tab?: ReturnsSettingsTab) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();
  const experience = useReturnsSettingsExperience();
  const showAction = experience.available && !experience.useNewUi;

  const tryNewSettings = useHandler(() => {
    experience.switchToNew();
    const path = routes.getSettingsSubRoute(RETURNS_CONSOLIDATED_SETTINGS_PATH);
    navigate(
      tab
        ? `${path}?${new URLSearchParams({ [RETURNS_SETTINGS_TAB_QUERY_PARAM]: tab })}`
        : path,
    );
  });

  const actions = useMemo<PageAction[] | undefined>(
    () =>
      showAction
        ? [
            {
              text: t("Try new settings"),
              hierarchy: "tertiary",
              onClick: tryNewSettings,
            },
          ]
        : undefined,
    [showAction, t, tryNewSettings],
  );
  usePageActions(actions);
}
